import { X } from "lucide-react";
import { useMemo } from "react";
import type { Machine } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Multi-facet filter state for the All Machines page.
 *
 * - `status` is single-select (mutually exclusive lifecycle states).
 * - `gpuTypes` is multi-select; an empty set means "no
 *   constraint" (matches all). Future facets (zone, owner) follow the same
 *   empty-set-is-unconstrained convention.
 *
 * Facets combine with AND across categories, OR within a category.
 */
export interface MachineFilterState {
  status: "running" | "free" | "stopped" | "mine" | "all";
  gpuTypes: Set<string>;
  /**
   * Reaper-protection facet. "any" = no constraint; "protected" / "unprotected"
   * narrow to machines the nightly reaper will skip / may delete.
   */
  skipDelete: "any" | "protected" | "unprotected";
}

export const DEFAULT_FILTERS: MachineFilterState = {
  status: "running",
  gpuTypes: new Set(),
  skipDelete: "any",
};

const SKIP_DELETE_OPTIONS: {
  key: MachineFilterState["skipDelete"];
  label: string;
}[] = [
  { key: "any", label: "Any" },
  { key: "protected", label: "Protected" },
  { key: "unprotected", label: "Reapable" },
];

const STATUS_OPTIONS: { key: MachineFilterState["status"]; label: string }[] = [
  { key: "running", label: "Running" },
  { key: "free", label: "Free" },
  { key: "stopped", label: "Stopped" },
  { key: "mine", label: "Mine" },
  { key: "all", label: "All" },
];

// Manual GPU ordering (oldest → newest) so the filter pills read as a
// generation timeline, not alphabetical (which puts B200 before H100).
const GPU_TYPE_ORDER = ["l4", "l40s", "a100", "a100-80g", "h100", "h200", "b200", "b300"];

function gpuTypeRank(gpuType: string): number {
  const index = GPU_TYPE_ORDER.indexOf(gpuType);
  return index === -1 ? GPU_TYPE_ORDER.length : index;
}

interface MachineFiltersProps {
  filters: MachineFilterState;
  onChange: (next: MachineFilterState) => void;
  machines: Machine[];
  currentUserId: number;
}

export function MachineFilters({
  filters,
  onChange,
  machines,
  currentUserId,
}: MachineFiltersProps) {
  const total = machines.length;
  const free = machines.filter(
    (machine) => !machine.allocated_to && machine.cloud_status === "running",
  ).length;
  const mine = machines.filter((machine) => machine.allocated_to === currentUserId).length;
  const allocated = machines.filter((machine) => machine.allocated_to != null).length;
  const stopped = machines.filter((machine) => machine.cloud_status === "stopped").length;

  // GPU pill list is dynamic — only show types present in the data, ordered
  // by generation (GPU_TYPE_ORDER); unknown SKUs fall to the end alphabetically.
  const gpuTypes = useMemo(() => {
    const seen = new Set<string>();
    for (const machine of machines) {
      if (machine.gpu_type) seen.add(machine.gpu_type);
    }
    return Array.from(seen).sort((firstGpu, secondGpu) => {
      const firstRank = gpuTypeRank(firstGpu);
      const secondRank = gpuTypeRank(secondGpu);
      if (firstRank !== secondRank) return firstRank - secondRank;
      return firstGpu.localeCompare(secondGpu);
    });
  }, [machines]);

  const isDefault =
    filters.status === DEFAULT_FILTERS.status &&
    filters.gpuTypes.size === 0 &&
    filters.skipDelete === DEFAULT_FILTERS.skipDelete;

  const setStatus = (status: MachineFilterState["status"]) => onChange({ ...filters, status });

  const toggleGpu = (gpuType: string) => {
    const next = new Set(filters.gpuTypes);
    if (next.has(gpuType)) next.delete(gpuType);
    else next.add(gpuType);
    onChange({ ...filters, gpuTypes: next });
  };

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-4 py-3 sm:px-6">
      <FacetGroup label="Status">
        {STATUS_OPTIONS.map(({ key, label }) => (
          <FilterPill key={key} active={filters.status === key} onClick={() => setStatus(key)}>
            {label}
          </FilterPill>
        ))}
      </FacetGroup>

      {gpuTypes.length > 0 && (
        <FacetGroup label="GPU">
          {gpuTypes.map((gpuType) => (
            <FilterPill
              key={gpuType}
              active={filters.gpuTypes.has(gpuType)}
              onClick={() => toggleGpu(gpuType)}
            >
              {gpuType.toUpperCase()}
            </FilterPill>
          ))}
        </FacetGroup>
      )}

      <FacetGroup label="Reaper">
        {SKIP_DELETE_OPTIONS.map(({ key, label }) => (
          <FilterPill
            key={key}
            active={filters.skipDelete === key}
            onClick={() => onChange({ ...filters, skipDelete: key })}
          >
            {label}
          </FilterPill>
        ))}
      </FacetGroup>

      {!isDefault && (
        <button
          type="button"
          onClick={() => onChange(DEFAULT_FILTERS)}
          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="h-3 w-3" />
          Reset
        </button>
      )}

      <div className="hidden flex-1 sm:block" />

      <span className="hidden text-[12px] text-muted-foreground sm:block">
        {total} machine{total !== 1 ? "s" : ""} &middot; {free} free &middot; {mine} mine &middot;{" "}
        {allocated} allocated &middot; {stopped} stopped
      </span>
    </div>
  );
}

function FacetGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="select-none text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
        {label}
      </span>
      <div className="flex flex-wrap gap-1">{children}</div>
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-muted/50 text-muted-foreground hover:bg-accent hover:text-accent-foreground",
      )}
    >
      {children}
    </button>
  );
}

export function filterMachines(
  machines: Machine[],
  filters: MachineFilterState,
  currentUserId: number,
): Machine[] {
  return machines.filter((machine) => {
    if (machine.provider !== "nebius") return false;
    // Status: single-select. "all" matches everything.
    if (filters.status === "running") {
      const transitionalOrRunning = ["running", "starting", "stopping"];
      if (!transitionalOrRunning.includes(machine.cloud_status)) return false;
    } else if (filters.status === "free") {
      if (machine.allocated_to || machine.cloud_status !== "running") return false;
    } else if (filters.status === "stopped") {
      if (machine.cloud_status !== "stopped") return false;
    } else if (filters.status === "mine") {
      if (machine.allocated_to !== currentUserId) return false;
    }

    // GPU: an empty set matches every model.
    if (filters.gpuTypes.size > 0 && !filters.gpuTypes.has(machine.gpu_type)) {
      return false;
    }

    // Reaper protection: single-select. "any" matches everything. The flag is
    // optional on the wire, so an absent value reads as unprotected.
    if (filters.skipDelete === "protected" && !machine.skip_delete) return false;
    if (filters.skipDelete === "unprotected" && machine.skip_delete) return false;
    return true;
  });
}
